import { useState } from 'react';
import { FitDetailPanel } from '@/components/FitDetailPanel';
import { useSavedMatches } from '@/hooks/useSavedMatches';
import { api } from '@/services/api';
import { garmentTypesForGroup } from '@/data/garmentTypes';
import type { ComparisonResult } from '@/types/domain';
import type { SavedMatch } from '@/types/history';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function garmentTypeName(match: SavedMatch): string | null {
  if (!match.garmentType) return null;
  return garmentTypesForGroup(match.category).find((t) => t.slug === match.garmentType)?.name ?? match.garmentType;
}

function SavedMatchCard({ match, onRemove }: { match: SavedMatch; onRemove: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [liveResult, setLiveResult] = useState<ComparisonResult | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const displayed = liveResult ?? match.result;

  async function compareAgain() {
    if (match.usedPassport || !match.sourceBrandSlug || !match.sourceSize) return;
    setRefreshing(true);
    const res = await api.compareBrandToBrand({
      audience: match.audience,
      category: match.category,
      garmentType: match.garmentType,
      sourceBrandSlug: match.sourceBrandSlug,
      sourceSize: match.sourceSize,
      targetBrandSlug: match.result.targetBrandSlug,
    });
    setRefreshing(false);
    if (res.success) {
      const data = res.data as { result: ComparisonResult };
      setLiveResult(data.result);
      setExpanded(true);
    }
  }

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-muted">Saved {formatDate(match.createdAt)}</p>
          <p className="mt-1 text-sm font-semibold text-ink">
            {match.sourceDescription} → {match.result.targetBrandName} {match.result.recommendedSize}
          </p>
          <p className="mt-1 text-xs text-muted">
            {match.category === 'tops' ? 'Top half' : 'Bottom half'}
            {garmentTypeName(match) ? ` · ${garmentTypeName(match)}` : ''} · {match.result.confidence} confidence
          </p>
        </div>
        <button
          type="button"
          aria-label="Remove this saved match"
          onClick={onRemove}
          className="shrink-0 text-sm text-muted hover:text-danger"
        >
          ✕
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="min-h-[36px] rounded-xl border border-stone-200 px-3 text-xs font-semibold text-ink hover:border-brand/40"
        >
          {expanded ? 'Hide fit map' : 'View fit map again'}
        </button>
        {!match.usedPassport && (
          <button
            type="button"
            onClick={compareAgain}
            disabled={refreshing}
            className="min-h-[36px] rounded-xl border border-stone-200 px-3 text-xs font-semibold text-ink hover:border-brand/40 disabled:opacity-50"
          >
            {refreshing ? 'Comparing…' : 'Compare again (live data)'}
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-4 max-w-xs border-t border-stone-100 pt-4">
          <FitDetailPanel audience={match.audience} category={match.category} result={displayed} />
        </div>
      )}
    </div>
  );
}

export function SavedMatchesPage() {
  const { savedMatches, remove } = useSavedMatches();

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink">Favourites</h2>
        <p className="mt-1 text-sm text-muted">
          Brand and size matches you've bookmarked from a comparison, so you can find them again without redoing the work.
        </p>
      </div>

      {savedMatches.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-stone-200 p-4 text-center text-sm text-muted">
          Nothing saved yet — tap "Save match" on a result's detail panel to bookmark it here.
        </p>
      ) : (
        <div className="space-y-3">
          {savedMatches.map((match) => (
            <SavedMatchCard key={match.id} match={match} onRemove={() => remove(match.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
